import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ApprovalWorkflow = ({ pendingExpenses, onApprove, onReject }) => {
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleApprove = (expenseId) => {
    onApprove(expenseId);
    setSelectedExpense(null);
  };

  const handleReject = (expenseId) => {
    if (rejectionReason?.trim()) {
      onReject(expenseId, rejectionReason);
      setSelectedExpense(null);
      setRejectionReason('');
    }
  };

  const handleBatchApprove = () => {
    const confirmApprove = window.confirm(
      `Aprovar ${pendingExpenses?.length} despesa(s) pendente(s)?`
    );
    if (confirmApprove) {
      pendingExpenses?.forEach(expense => onApprove(expense?.id));
    }
  };

  if (pendingExpenses?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Nenhuma Despesa Pendente
        </h3>
        <p className="text-sm text-muted-foreground">
          Todas as despesas foram aprovadas ou rejeitadas
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Aprovação de Despesas
            </h3>
            <p className="text-sm text-muted-foreground">
              {pendingExpenses?.length} despesa(s) aguardando aprovação
            </p>
          </div>
          <Button
            variant="default"
            iconName="CheckCheck"
            onClick={handleBatchApprove}
          >
            Aprovar Todas
          </Button>
        </div>

        <div className="space-y-3">
          {pendingExpenses?.map((expense) => (
            <div
              key={expense?.id}
              className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name={expense?.categoryIcon} size={20} className="text-primary" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {expense?.vendor}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {new Date(expense.date)?.toLocaleDateString('pt-BR')} • {expense?.category}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {expense?.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-foreground">
                      R$ {expense?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                    {expense?.client && (
                      <span className="text-muted-foreground">
                        Cliente: {expense?.client}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {expense?.receipt && (
                    <button
                      onClick={() => setSelectedExpense(expense)}
                      className="p-2 rounded hover:bg-muted transition-colors"
                      title="Ver Comprovante"
                    >
                      <Image
                        src={expense?.receipt}
                        alt={expense?.receiptAlt}
                        className="w-16 h-16 rounded object-cover border border-border"
                      />
                    </button>
                  )}

                  <div className="flex flex-col gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Check"
                      onClick={() => handleApprove(expense?.id)}
                    >
                      Aprovar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="X"
                      onClick={() => setSelectedExpense(expense)}
                    >
                      Rejeitar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {selectedExpense && (
        <div className="fixed inset-0 bg-black/50 z-[1020] flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                Detalhes da Despesa
              </h2>
              <button
                onClick={() => setSelectedExpense(null)}
                className="p-1 rounded-md hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fornecedor</label>
                  <p className="text-foreground">{selectedExpense?.vendor}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Valor</label>
                  <p className="text-foreground font-semibold">
                    R$ {selectedExpense?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data</label>
                  <p className="text-foreground">
                    {new Date(selectedExpense.date)?.toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Categoria</label>
                  <p className="text-foreground">{selectedExpense?.category}</p>
                </div>
              </div>

              {selectedExpense?.receipt && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">
                    Comprovante
                  </label>
                  <Image
                    src={selectedExpense?.receipt}
                    alt={selectedExpense?.receiptAlt}
                    className="w-full h-64 object-contain rounded border border-border"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Motivo da Rejeição (Opcional)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  placeholder="Digite o motivo da rejeição..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedExpense(null)}
                  fullWidth
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(selectedExpense?.id)}
                  fullWidth
                  iconName="X"
                >
                  Rejeitar
                </Button>
                <Button
                  variant="default"
                  onClick={() => handleApprove(selectedExpense?.id)}
                  fullWidth
                  iconName="Check"
                >
                  Aprovar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalWorkflow;